/**
 * Connected view — the tenant's configured source connections as cards.
 *
 * Developer+ surface (no User access — enforced server-side). Per-card
 * Test re-probes the connection and refreshes its health; Delete
 * (admin-only) prompts for an audit reason; Configure opens the detail at
 * /integration/connections/:id.
 */

import { useCallback, useEffect, useState } from "react";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconPlugConnected, IconSettings, IconTrash } from "@tabler/icons-react";
import { Link as RouterLink } from "react-router-dom";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { classificationColor } from "@/shared/ui/palette";
import { useMe } from "@/features/users/useMe";
import { promptForReason } from "@/shared/ui/promptForReason";
import { toastApiError, toastSuccess, toastWarning } from "@/shared/ui/toast";
import {
  deleteConnection,
  healthColor,
  listCatalog,
  listConnections,
  testSavedConnection,
  type ConnectionOut,
} from "@/shared/api/integration";
import { SourceTile } from "./SourceTile";

// Used until the catalog loads, and if it can't be fetched.
const FALLBACK_SOURCE_OPTIONS = [
  { value: "veeva", label: "Veeva Vault" },
  { value: "blob", label: "Azure Blob Storage" },
];

/** Trim a server ISO timestamp to a readable "YYYY-MM-DD HH:mm UTC". */
function formatTested(iso: string | null): string {
  if (!iso) return "never";
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return `${parsed.toISOString().slice(0, 16).replace("T", " ")} UTC`;
}

export function ConnectionsList() {
  const getToken = useAccessToken();
  const { data: me } = useMe();
  const isAdmin = me?.roles.includes("administrator") ?? false;

  const [items, setItems] = useState<ConnectionOut[]>([]);
  const [sourceFilter, setSourceFilter] = useState<string | null>(null);
  const [sourceOptions, setSourceOptions] = useState(FALLBACK_SOURCE_OPTIONS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter options come from the catalog's connectable types (best-effort —
  // falls back to the static list if the catalog can't be loaded).
  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const token = await getToken();
        const catalog = await listCatalog(token);
        const options = catalog.items
          .filter((entry) => entry.status === "available")
          .map((entry) => ({ value: entry.source_type, label: entry.display_name }));
        if (active && options.length > 0) setSourceOptions(options);
      } catch {
        // Keep the fallback options.
      }
    })();
    return () => {
      active = false;
    };
  }, [getToken]);

  const load = useCallback(
    async (filter: string | null) => {
      setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        const page = await listConnections(token, filter);
        setItems(page.items);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    },
    [getToken],
  );

  useEffect(() => {
    void load(sourceFilter);
  }, [load, sourceFilter]);

  const onTest = async (conn: ConnectionOut) => {
    try {
      const token = await getToken();
      const result = await testSavedConnection(token, conn.connection_id);
      if (result.ok) {
        toastSuccess("Connection healthy", `${conn.display_name} responded OK.`);
      } else {
        toastWarning("Connection failing", `${conn.display_name} could not be reached.`);
      }
      void load(sourceFilter);
    } catch (err) {
      toastApiError("Test failed", err);
    }
  };

  const onDelete = async (conn: ConnectionOut) => {
    const reason = await promptForReason(
      `Delete connection "${conn.display_name}"`,
      "Audit reason",
      {
        warning: "This removes the connection and disables its stored credential.",
        confirmLabel: "Delete",
      },
    );
    if (!reason) return;
    try {
      const token = await getToken();
      await deleteConnection(token, conn.connection_id, reason);
      toastWarning("Connection deleted", `${conn.display_name} was removed.`);
      void load(sourceFilter);
    } catch (err) {
      toastApiError("Delete failed", err);
    }
  };

  return (
    <Stack gap="md">
      <Group>
        <Select
          label="Source type"
          placeholder="All"
          value={sourceFilter}
          onChange={setSourceFilter}
          data={sourceOptions}
          clearable
          w={220}
        />
      </Group>

      {error && (
        <Alert variant="light" color="red" title="Could not load connections">
          {error}
        </Alert>
      )}

      {loading ? (
        <Group justify="center" p="xl">
          <Loader size="sm" />
        </Group>
      ) : items.length === 0 ? (
        <Paper withBorder radius="md" p="xl">
          <Text c="dimmed" ta="center">
            No connections yet. Add one from Integrations → Add connection.
          </Text>
        </Paper>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg" verticalSpacing="lg">
          {items.map((c) => {
            return (
              <Card
                key={c.connection_id}
                withBorder
                radius="md"
                padding="lg"
                style={{ minHeight: 210 }}
              >
                <Stack gap="sm" h="100%">
                  <Group justify="space-between" wrap="nowrap" align="flex-start">
                    <Group gap="sm" wrap="nowrap" align="center">
                      <SourceTile sourceType={c.source_type} />
                      <div>
                        <Text fw={600} lineClamp={1}>
                          {c.display_name}
                        </Text>
                        <Text size="xs" c="dimmed" tt="uppercase">
                          {c.source_type}
                        </Text>
                      </div>
                    </Group>
                    <Badge variant="dot" color={healthColor(c.health_status)}>
                      {c.health_status}
                    </Badge>
                  </Group>

                  <Group gap={6}>
                    <Badge
                      variant="light"
                      color={classificationColor(c.classification)}
                      size="sm"
                      radius="sm"
                    >
                      {c.classification}
                    </Badge>
                  </Group>

                  <Text size="xs" c="dimmed" style={{ flex: 1 }}>
                    Last tested: {formatTested(c.last_tested_at_utc)}
                  </Text>

                  <Group justify="space-between" wrap="nowrap">
                    <Button
                      component={RouterLink}
                      to={`/integration/connections/${c.connection_id}`}
                      leftSection={<IconSettings size={16} />}
                      variant="light"
                      size="xs"
                    >
                      Configure
                    </Button>
                    <Group gap={4} wrap="nowrap">
                      <Tooltip label="Test connection">
                        <ActionIcon
                          variant="subtle"
                          color="brand"
                          onClick={() => void onTest(c)}
                          aria-label="Test connection"
                        >
                          <IconPlugConnected size={18} />
                        </ActionIcon>
                      </Tooltip>
                      {isAdmin && (
                        <Tooltip label="Delete connection">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => void onDelete(c)}
                            aria-label="Delete connection"
                          >
                            <IconTrash size={18} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Group>
                  </Group>
                </Stack>
              </Card>
            );
          })}
        </SimpleGrid>
      )}
    </Stack>
  );
}
